import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Button,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    Pagination,
    PaginationItem,
    IconButton,
} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PersonIcon from '@mui/icons-material/Person';
import StorageIcon from '@mui/icons-material/Storage';
import RefreshIcon from '@mui/icons-material/Refresh';
import Navbar from "../components/Navbar.js";
import { backendRoot } from "../config";

export default function Reports() {
    const navigate = useNavigate();

    // State for data upload log
    const [uploadLogs, setUploadLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [logsPerPage] = useState(15);

    const handleBackToAppendData = () => {
        navigate('/append-data');
    };

    // Fetch data upload log from backend API
    const fetchUploadLogs = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(
                `${backendRoot}/api/data-upload-log/`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            console.log('Fetched upload logs data:', data);
            
            // Handle paginated vs non-paginated response
            if (data && data.results && Array.isArray(data.results)) {
                setUploadLogs(data.results);
            } else if (Array.isArray(data)) {
                setUploadLogs(data);
            } else {
                setUploadLogs([]);
            }
        } catch (error) {
            console.error('Error fetching upload logs:', error);
            setError(`Failed to fetch upload logs: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Fetch data on component mount
    useEffect(() => {
        fetchUploadLogs();
    }, []);

    const handlePageChange = (event, value) => {
        setPage(value);
        window.scrollTo(0, 0);
    };

    return (
        <Box sx={{
            background: "#f8f9fa",
            minHeight: "100vh"
        }}>
            <Navbar />

            {/* Spacing below navbar */}
            <Box sx={{ py: 2 }} />

            {/* Header Section */}
            <Box sx={{
                background: "#EFF6FF",
                py: 1.5,
                mb: 2,
                boxShadow: '0 2px 8px rgba(255, 214, 0, 0.2)',
                borderRadius: 2
            }}>
                <Box sx={{ px: { xs: 2, md: 4 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={handleBackToAppendData}
                            sx={{
                                backgroundColor: '#1a1a1a',
                                color: '#60A5FA',
                                '&:hover': { backgroundColor: '#2c2c2c' },
                                fontWeight: 600,
                                borderRadius: 2,
                                px: 3,
                                py: 1
                            }}
                        >
                            Back to Append Data
                        </Button>

                        <IconButton
                            onClick={fetchUploadLogs}
                            sx={{
                                backgroundColor: 'rgba(26, 26, 26, 0.1)',
                                color: '#1a1a1a',
                                '&:hover': { backgroundColor: 'rgba(26, 26, 26, 0.2)' },
                                borderRadius: 2,
                                p: 1
                            }}
                        >
                            <RefreshIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ textAlign: 'center', mt: 1 }}>
                        <Typography variant="h5" sx={{
                            fontWeight: 700,
                            color: '#1a1a1a'
                        }}>
                            DATA UPLOAD LOG
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Error Alert */}
            {error && (
                <Box sx={{ px: { xs: 2, md: 4 }, mb: 3 }}>
                    <Alert
                        severity="error"
                        onClose={() => setError(null)}
                        sx={{
                            borderRadius: 3,
                            boxShadow: '0 4px 15px rgba(244, 67, 54, 0.2)'
                        }}
                    >
                        {error}
                    </Alert>
                </Box>
            )}


            {/* Main Content - Data Upload Log Table */}
            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4 }}>

                <Paper
                    elevation={3}
                    sx={{
                        borderRadius: 2,
                        overflow: 'hidden',
                        background: 'white',
                        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)'
                    }}
                >
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{
                                    background: '#60A5FA',
                                    '& .MuiTableCell-root': {
                                        color: '#1a1a1a',
                                        fontWeight: 600,
                                        fontSize: '0.9rem',
                                        borderBottom: 'none'
                                    }
                                }}>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <DescriptionIcon sx={{ fontSize: 16 }} />
                                            ID
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <StorageIcon sx={{ fontSize: 16 }} />
                                            Source File Name
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <StorageIcon sx={{ fontSize: 16 }} />
                                            Destination Table
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <DescriptionIcon sx={{ fontSize: 16 }} />
                                            Description
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <PersonIcon sx={{ fontSize: 16 }} />
                                            Processed By
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <ScheduleIcon sx={{ fontSize: 16 }} />
                                            Processed At
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <CheckCircleIcon sx={{ fontSize: 16 }} />
                                            Status
                                        </Box>
                                    </TableCell>
                                    <TableCell>Rows Inserted</TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <ErrorIcon sx={{ fontSize: 16 }} />
                                            Error Message
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                                            <Box sx={{
                                                display: "flex",
                                                flexDirection: 'column',
                                                justifyContent: "center",
                                                alignItems: 'center',
                                                gap: 2
                                            }}>
                                                <CircularProgress
                                                    sx={{
                                                        color: '#60A5FA',
                                                        '& .MuiCircularProgress-circle': {
                                                            strokeWidth: 3
                                                        }
                                                    }}
                                                    size={40}
                                                />
                                                <Typography sx={{
                                                    color: '#1a1a1a',
                                                    fontWeight: 500,
                                                    fontSize: '1rem'
                                                }}>
                                                    Loading upload logs...
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ) : uploadLogs.length > 0 ? (
                                    (() => {
                                        // Sort by processed_at in descending order (latest first)
                                        const sortedLogs = [...uploadLogs].sort((a, b) =>
                                            new Date(b.processed_at) - new Date(a.processed_at)
                                        );

                                        // Pagination logic
                                        const totalPages = Math.ceil(sortedLogs.length / logsPerPage);
                                        const indexOfLastLog = page * logsPerPage;
                                        const indexOfFirstLog = indexOfLastLog - logsPerPage;
                                        const currentLogs = sortedLogs.slice(indexOfFirstLog, indexOfLastLog);

                                        // Always show exactly 15 rows (filled + empty)
                                        const rowsToShow = [...currentLogs];
                                        const emptyRowsToAdd = 15 - currentLogs.length;
                                        for (let i = 0; i < emptyRowsToAdd; i++) {
                                            rowsToShow.push({
                                                row_id: `empty-${i}`,
                                                source_file_name: '',
                                                destination_table: '',
                                                description: '',
                                                processed_by: '',
                                                processed_at: '',
                                                status: '',
                                                rows_inserted: '',
                                                error_message: '',
                                                isEmpty: true
                                            });
                                        }

                                        return (
                                            <>
                                                {rowsToShow.map((log, index) => (
                                                    <TableRow
                                                        key={log.row_id}
                                                        hover={!log.isEmpty}
                                                        sx={{
                                                            "&:hover": {
                                                                backgroundColor: log.isEmpty ? "transparent" : "rgba(102, 126, 234, 0.08)",
                                                                transform: log.isEmpty ? 'none' : 'scale(1.001)'
                                                            },
                                                            height: "20px",
                                                            backgroundColor: index % 2 === 0 ? 'rgba(255, 255, 255, 0.5)' : 'rgba(248, 249, 250, 0.8)',
                                                            transition: 'all 0.2s ease',
                                                            borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
                                                            '& .MuiTableCell-root': {
                                                                borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
                                                                fontSize: '0.8rem'
                                                            }
                                                        }}
                                                    >
                                                        <TableCell sx={{ height: "20px", fontWeight: 600, color: '#2c3e50' }}>
                                                            {log.isEmpty ? '\u00A0' : log.row_id}
                                                        </TableCell>
                                                        <TableCell sx={{
                                                            height: "20px",
                                                            fontSize: '0.8rem',
                                                            wordBreak: 'break-word'
                                                        }}>
                                                            {log.isEmpty ? '\u00A0' : log.source_file_name}
                                                        </TableCell>
                                                        <TableCell sx={{ height: "20px", fontSize: '0.8rem' }}>
                                                            {log.isEmpty ? '\u00A0' : log.destination_table}
                                                        </TableCell>
                                                        <TableCell sx={{
                                                            height: "20px",
                                                            fontSize: '0.8rem',
                                                            wordBreak: 'break-word'
                                                        }}>
                                                            {log.isEmpty ? '\u00A0' : (log.description || 'N/A')}
                                                        </TableCell>
                                                        <TableCell sx={{ height: "20px" }}>
                                                            {log.isEmpty ? '\u00A0' : (log.processed_by || 'N/A')}
                                                        </TableCell>
                                                        <TableCell sx={{ height: "20px", fontSize: '0.75rem' }}>
                                                            {log.isEmpty ? '\u00A0' : (
                                                                log.processed_at ? new Date(log.processed_at).toLocaleString() : 'N/A'
                                                            )}
                                                        </TableCell>
                                                        <TableCell sx={{ height: "20px", fontSize: '0.8rem' }}>
                                                            {log.isEmpty ? '\u00A0' : log.status}
                                                        </TableCell>
                                                        <TableCell sx={{ height: "20px", fontWeight: 600, color: '#2c3e50' }}>
                                                            {log.isEmpty ? '\u00A0' : (log.rows_inserted || 'N/A')}
                                                        </TableCell>
                                                        <TableCell sx={{
                                                            height: "20px",
                                                            fontSize: "0.75rem",
                                                            wordBreak: "break-word",
                                                        }}>
                                                            {log.isEmpty ? (
                                                                "\u00A0"
                                                            ) : typeof log.error_message === "object" ? (
                                                                log.error_message?.detail || JSON.stringify(log.error_message)
                                                            ) : (
                                                                log.error_message || "N/A"
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                                {sortedLogs.length > 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={9} sx={{ p: 0, border: 'none' }}>
                                                            <Box sx={{
                                                                display: 'flex',
                                                                justifyContent: 'center',
                                                                alignItems: 'center',
                                                                p: 2,
                                                                background: 'rgba(255, 214, 0, 0.05)',
                                                                borderTop: '1px solid rgba(255, 214, 0, 0.2)'
                                                            }}>
                                                                <Pagination
                                                                    count={totalPages}
                                                                    page={page}
                                                                    onChange={handlePageChange}
                                                                    renderItem={(item) => (
                                                                        <PaginationItem
                                                                            components={{ previous: ArrowBackIcon, next: ArrowForwardIcon }}
                                                                            sx={{
                                                                                '&.Mui-selected': {
                                                                                    background: '#60A5FA',
                                                                                    color: '#fff',
                                                                                    fontWeight: 'bold',
                                                                                    borderRadius: 2,
                                                                                    '&:hover': {
                                                                                        background: '#3B82F6'
                                                                                    }
                                                                                },
                                                                                '& .MuiPaginationItem-icon': { color: '#1a1a1a' },
                                                                                borderRadius: 2,
                                                                                margin: '0 2px',
                                                                                '&:hover': {
                                                                                    backgroundColor: 'rgba(255, 214, 0, 0.2)'
                                                                                },
                                                                                transition: 'all 0.2s ease'
                                                                            }}
                                                                            {...item}
                                                                        />
                                                                    )}
                                                                />
                                                                <Typography sx={{
                                                                    ml: 3,
                                                                    color: '#1a1a1a',
                                                                    fontWeight: 600,
                                                                    fontSize: '0.9rem'
                                                                }}>
                                                                    Page {page} of {totalPages}
                                                                </Typography>
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </>
                                        );
                                    })()
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                                            <Box sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                gap: 2
                                            }}>
                                                <DescriptionIcon sx={{
                                                    fontSize: 64,
                                                    color: 'rgba(255, 214, 0, 0.3)',
                                                    opacity: 0.7
                                                }} />
                                                <Typography sx={{
                                                    color: '#1a1a1a',
                                                    fontWeight: 500,
                                                    fontSize: '1.1rem'
                                                }}>
                                                    No upload logs found
                                                </Typography>
                                                <Typography sx={{
                                                    color: 'rgba(26, 26, 26, 0.6)',
                                                    fontSize: '0.9rem'
                                                }}>
                                                    Upload some data to see reports here
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Box>
        </Box>
    );
}